/* ===================================================================
   kuoca-main.js — design-bridge apply(main) 스크롤 엔진 이식본
   출처: lukuku-design/project/kuoca/preview/main.html (승인 2026-07-22,
   문서 높이 불변 v2). 원칙: 커버 유닛 = 자연 높이 + 연속 배치 + sticky
   음수 top(load/resize 에서만 계산), Hold 는 이전 유닛 ≥ 뷰포트일 때만,
   스크림 = 이전 유닛 하단 침투율(1/3 게이트, 최대 0.42, incoming z − 1),
   헤더 상태머신 = 색(is-solid, 히어로 하단 경계) × 표시(is-hidden, 방향).

   Shopify 적응:
   - 커버 유닛/패널 = 섹션 루트의 data-kuoca-cover / data-kuoca-panel 를
     .shopify-section 래퍼로 승격해 sticky·z-index 를 래퍼에 적용한다
     (내부 요소에 걸면 래퍼가 sticky 경계가 되어 동작 불가).
   - z-index 는 문서 순서로 부여(패널 20/30/40…, 유닛 전용 1) — 에디터에서
     섹션을 재배열해도 스태킹 순서가 따라온다.
   - 스크림/Hold 는 JS 가 생성·삽입 (섹션 마크업 오염 없음).
   - 히어로 없는 페이지(계정 등): 헤더 상시 is-solid + body.kuoca-no-hero.
   =================================================================== */
(function () {
  'use strict';

  var isThemeEditor = !!(window.Shopify && window.Shopify.designMode);
  var header = null;
  var wrapOf = function (el) {
    return el.closest('.shopify-section') || el;
  };

  var coverUnits = [];
  var stackedPanels = [];
  var heroSection = null;
  var firstCoverPanel = null;
  var pageScrim = null;
  var transitionHolds = new Map();
  var editorResizeObserver = null;
  var editorResizeTick = 0;
  var editorRebuildTick = 0;
  var editorSelectedSectionId = null;
  var headerPageShowBound = false;

  function wrappedElements(selector) {
    return Array.prototype.reduce.call(
      document.querySelectorAll(selector),
      function (items, element) {
        var wrapper = wrapOf(element);
        if (items.indexOf(wrapper) === -1) items.push(wrapper);
        return items;
      },
      [],
    );
  }

  /* 테마 편집기는 섹션 설정 변경 시 해당 DOM 만 교체한다. 기존 스캐폴드를 제거하고
     현재 DOM 기준으로 다시 구성해야 stale wrapper/hold/z-index 가 남지 않는다. */
  function clearStackingScaffold() {
    coverUnits.concat(stackedPanels).forEach(function (wrapper) {
      wrapper.classList.remove('kuoca-unit', 'kuoca-panel');
      wrapper.style.removeProperty('top');
      wrapper.style.removeProperty('z-index');
    });

    document
      .querySelectorAll("[data-kuoca-generated='hold'], [data-kuoca-generated='scrim']")
      .forEach(function (element) {
        element.remove();
      });

    if (editorResizeObserver) editorResizeObserver.disconnect();
    if (editorResizeTick) window.cancelAnimationFrame(editorResizeTick);
    editorResizeObserver = null;
    editorResizeTick = 0;
    transitionHolds = new Map();
    pageScrim = null;
  }

  function buildStackingScaffold() {
    clearStackingScaffold();

    header = document.querySelector('.kuoca-header');
    coverUnits = wrappedElements('[data-kuoca-cover]');
    stackedPanels = wrappedElements('[data-kuoca-panel]');
    heroSection = document.querySelector('[data-kuoca-hero]');
    firstCoverPanel = stackedPanels[0] || null;

    document.body.classList.toggle('kuoca-no-hero', !!header && !heroSection);
    document.body.classList.toggle('kuoca-cover', coverUnits.length > 0);

    coverUnits.forEach(function (wrapper) {
      wrapper.classList.add('kuoca-unit');
    });
    stackedPanels.forEach(function (wrapper, index) {
      wrapper.classList.add('kuoca-panel');
      wrapper.style.zIndex = String(20 + index * 10);
    });
    coverUnits.forEach(function (wrapper) {
      if (stackedPanels.indexOf(wrapper) === -1) wrapper.style.zIndex = '1';
    });

    if (stackedPanels.length) {
      pageScrim = document.createElement('div');
      pageScrim.className = 'kuoca-scrim';
      pageScrim.dataset.kuocaGenerated = 'scrim';
      pageScrim.setAttribute('aria-hidden', 'true');
      document.body.appendChild(pageScrim);
    }

    stackedPanels.forEach(function (panel) {
      /* 연속 스크롤 패널은 리빌이 아니다 — Hold(체류 스페이서)를 만들지 않는다.
         Hold 가 있으면 앞 섹션과의 사이가 벌어져 "이어서 함께 스크롤"이 깨진다. */
      if (isContinuousPanel(panel)) return;
      var hold = document.createElement('div');
      hold.className = 'kuoca-hold';
      hold.dataset.kuocaGenerated = 'hold';
      hold.setAttribute('aria-hidden', 'true');
      panel.before(hold);
      transitionHolds.set(panel, hold);
    });
  }

  /* 스태킹 z-index 만 받고 리빌 연출(Hold·스크림)은 받지 않는 패널.
     앞 섹션이 핀되지 않은 채로 이어서 함께 스크롤되는 구간에 쓴다.
     z-index 는 여전히 필요하다 — 아래에 sticky 로 핀된 커버 유닛이 깔려 있어서,
     포지션/레벨이 없으면 그 위로 지나가지 못하고 가려진다. */
  function isContinuousPanel(wrapper) {
    return !!wrapper.querySelector('[data-kuoca-panel-continuous]');
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }
  function readZIndex(element, fallback) {
    var parsed = Number.parseInt(window.getComputedStyle(element).zIndex, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  /* 각 커버 유닛 sticky 음수 top — top = min(0, 뷰포트 − 유닛높이). load/resize 에서만. */
  function updateStickyTops() {
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    coverUnits.forEach(function (unit) {
      unit.style.top = Math.round(Math.min(0, viewportHeight - unit.offsetHeight)) + 'px';
    });

    /* 전역 헤더 실측 높이 → --header-h (접힌 섹션 타이틀 바 = 헤더 높이 동기) */
    if (header) {
      document.documentElement.style.setProperty(
        '--header-h',
        Math.round(header.getBoundingClientRect().height) + 'px',
      );
    }

    /* 마퀴 실측 높이 → --kuoca-marquee-h. 히어로가 "화면 높이에 딱 맞게" 서려면
       100vh 가 아니라 100vh − 마퀴여야 한다. 마퀴는 히어로 위 흐름에 있어서, 그냥
       100vh 로 두면 그만큼 아래가 잘리고 하단 인디케이터가 화면 밖으로 밀린다.
       마퀴는 문구가 비면 렌더되지 않고 mo 에서는 2줄이라 높이가 달라진다 — 상수 금지. */
    var marquee = document.querySelector('.kuoca-marquee');
    document.documentElement.style.setProperty(
      '--kuoca-marquee-h',
      (marquee ? Math.round(marquee.getBoundingClientRect().height) : 0) + 'px',
    );

    /* 문서 높이 불변 v2: 접힌 헤더의 박스 높이를 펼침 실측값(--hdr-h)으로 고정,
       접힘은 음수 top 으로만 — flow 불변이라 min-height 잠금이 필요 없다. */
    document.querySelectorAll('.kuoca-collection, .kuoca-about').forEach(function (section) {
      var hdr = section.querySelector('.kuoca-collection__header');
      if (!hdr) return;
      var wasStuck = section.classList.contains('is-stuck');
      var wasCapped = section.classList.contains('is-capped');
      /* 서브텍스트도 자기 트랜지션을 갖는다 — 헤더만 끄면 클래스를 떼는 순간 문단이
         0 에서 펼쳐지기 시작해, 같은 프레임에 재면 접힌 높이를 읽는다. 같이 끈다. */
      var paras = Array.prototype.slice.call(hdr.querySelectorAll('p'));
      hdr.style.transition = 'none';
      paras.forEach(function (para) {
        para.style.transition = 'none';
      });
      section.classList.remove('is-stuck');
      section.classList.remove('is-capped');
      section.style.removeProperty('--hdr-h');
      paras.forEach(function (para) {
        para.style.removeProperty('--sub-h');
      });
      var expanded = Math.round(hdr.getBoundingClientRect().height);
      /* 섹션에 주입 — 헤더와 ::after 보상이 함께 상속받아 읽는다 */
      section.style.setProperty('--hdr-h', expanded + 'px');

      /* 서브텍스트 접힘용 실측 높이. max-height 를 넉넉한 상수(96px)로 두면 실제 높이가
         그보다 작을 때(1줄 = 48px) 앞 절반이 허공에서 흐르다 뒤에서 몰아 줄어들어
         이미지가 툭 튀어 보인다. 문단마다 제 높이를 재서 그 값에서 0 으로 접는다.
         about 은 richtext 라 문단이 여럿일 수 있어 요소 단위로 주입한다. */
      paras.forEach(function (para) {
        para.style.setProperty('--sub-h', Math.ceil(para.getBoundingClientRect().height) + 'px');
      });
      if (wasStuck) section.classList.add('is-stuck');
      if (wasCapped) section.classList.add('is-capped');
      void hdr.offsetHeight; /* 클래스 복원을 트랜지션 없이 커밋 (측정·복원은 페인트 전 동기) */
      hdr.style.transition = '';
      paras.forEach(function (para) {
        para.style.transition = '';
      });
    });

    /* Hold: 이전 유닛이 뷰포트 이상일 때만 노출, 짧으면 0 (Loewe 는 hold 없이 연속 배치) */
    transitionHolds.forEach(function (hold, panel) {
      var prevUnit = coverUnits[Math.min(stackedPanels.indexOf(panel), coverUnits.length - 1)];
      var show = prevUnit && prevUnit.offsetHeight >= viewportHeight;
      hold.style.height = show ? '' : '0px';
    });
  }

  /* 스크림: 기준은 뷰포트가 아니라 "이전 유닛의 하단" 침투율 (p 1/3 부터 점증, 2/3 에서 0.42) */
  function updateSectionScrim() {
    if (!pageScrim) return;
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    var scrimOpacity = 0;
    var scrimHeight = 0;
    var incomingSection = null;

    stackedPanels.forEach(function (section, index) {
      /* 연속 스크롤 패널은 덮으며 올라오는 게 아니라 앞 섹션과 함께 흐른다 — 스크림 없음. */
      if (isContinuousPanel(section)) return;
      var rect = section.getBoundingClientRect();
      if (rect.top <= 0 || rect.bottom <= 0) return;
      var prevUnit = coverUnits[Math.min(index, coverUnits.length - 1)];
      if (!prevUnit) return;
      var prevBottom = Math.min(prevUnit.getBoundingClientRect().bottom, viewportHeight);
      if (rect.top >= prevBottom) return;

      var penetration = clamp((prevBottom - rect.top) / Math.max(prevBottom, 1), 0, 1);
      var progress = clamp((penetration - 0.33) / 0.33, 0, 1) * 0.42;
      if (progress > scrimOpacity) {
        scrimOpacity = progress;
        scrimHeight = clamp(rect.top, 0, viewportHeight);
        incomingSection = section;
      }
    });

    /* 스크림은 "올라오는 섹션 바로 아래" 레이어 — 새 섹션은 절대 덮지 않는다 */
    if (incomingSection) {
      pageScrim.style.zIndex = String(readZIndex(incomingSection, 20) - 1);
    }
    pageScrim.style.setProperty('--scrim-height', Math.round(scrimHeight) + 'px');
    pageScrim.style.opacity = String(scrimOpacity);
  }

  /* ---- 전역 헤더 v2 (Loewe 실측 패턴) ---- */
  var HEADER_TOP_ZONE = 4;
  var HEADER_HIDE_DEADZONE = 4;
  var HEADER_SHOW_DEADZONE = 1;
  var lastScrollY = window.scrollY;
  var marqueeWasVisible = false;
  var lastMarqueeTop = -1;
  var scrollTicking = false;

  function updateGlobalHeader() {
    if (!header) return;
    var currentY = window.scrollY;
    var delta = currentY - lastScrollY;
    var headerHeight = header.offsetHeight || 84;
    var marquee = document.querySelector('.kuoca-marquee');
    var marqueeVisibleHeight = marquee ? Math.max(0, Math.round(marquee.getBoundingClientRect().bottom)) : 0;
    /* 값이 실제로 바뀔 때만 쓴다 — 같은 값 재설정도 스타일 재계산을 부른다. */
    if (marqueeVisibleHeight !== lastMarqueeTop) {
      header.style.setProperty('--kuoca-header-top', marqueeVisibleHeight + 'px');
      lastMarqueeTop = marqueeVisibleHeight;
    }
    var marqueeIsVisible = marqueeVisibleHeight > 0;
    header.classList.toggle('is-marquee-tracking', marqueeIsVisible);

    /* 색 상태: 히어로 하단 경계 기반 (transparent→solid). 히어로 없는 페이지는 상시 solid. */
    var isSolid;
    if (heroSection) {
      var heroGone = heroSection.getBoundingClientRect().bottom <= headerHeight;
      var heroCovered = firstCoverPanel && firstCoverPanel.getBoundingClientRect().top <= headerHeight;
      isSolid = heroGone || heroCovered;
    } else {
      isSolid = true;
    }
    header.classList.toggle('is-solid', isSolid);

    if (header.classList.contains('is-navigating')) {
      header.classList.remove('is-hidden');
      lastScrollY = currentY;
      return;
    }

    /* 마퀴가 보이는 동안에는 top 위치만 따라가고 transform 숨김은 실행하지 않는다.
       마퀴가 사라지는 경계 프레임도 한 번 건너뛰어 top과 transform의 동시 변경을 막는다. */
    if (marqueeIsVisible) {
      header.classList.remove('is-hidden');
      marqueeWasVisible = true;
      lastScrollY = currentY;
      return;
    }
    if (marqueeWasVisible) {
      header.classList.remove('is-hidden');
      marqueeWasVisible = false;
      lastScrollY = currentY;
      return;
    }

    /* 표시 상태: 아래 스크롤 → 숨김, 위 스크롤 → 즉시 재노출 (재노출 헤더 z100 이 섹션 타이틀 z60 을 덮어 "교체") */
    var nearTop = currentY <= HEADER_TOP_ZONE;
    if (nearTop) {
      header.classList.remove('is-hidden');
    } else if (delta > HEADER_HIDE_DEADZONE) {
      header.classList.add('is-hidden');
    } else if (delta < -HEADER_SHOW_DEADZONE) {
      header.classList.remove('is-hidden');
    }
    marqueeWasVisible = false;
    lastScrollY = currentY;
  }

  /* 섹션 타이틀 sticky 축소 — about 포함 (G-14 재결정 2026-07-23: 문서 높이 불변 v2 로
     축소가 flow 를 안 바꾸므로 진동 사유 소멸, Figma about sticky variant 복원) */
  function updateStickySections() {
    document.querySelectorAll('.kuoca-collection, .kuoca-about').forEach(function (section) {
      var stickyHeader = section.querySelector('.kuoca-collection__header');
      if (!stickyHeader) return;
      /* 타이틀 축소를 쓰지 않는 섹션(data-kuoca-sticky-title-off). 헤더가 CSS 에서 static
         이라 축소할 대상 자체가 없다. 클래스가 남아 있으면 서브텍스트만 접히므로 지운다.
         이 옵트아웃이 필요한 이유: 유닛 높이가 뷰포트와 같으면 sticky top 이 0 이라
         고정되는 내내 sectionRect.top 이 0 이고, isCapped 조건(top > barH − hdrH + 2)이
         항상 참이 된다. 한 번 접히면 스크롤을 올려도 접힌 채로 머문다 — 다른 섹션은
         top 이 음수라 그 구간을 지나면 자연히 풀린다. */
      if (section.hasAttribute('data-kuoca-sticky-title-off')) {
        section.classList.remove('is-stuck');
        section.classList.remove('is-capped');
        return;
      }
      var sectionRect = section.getBoundingClientRect();
      var sectionStyle = getComputedStyle(section);
      /* 해제 임계는 "보이는 바 높이"(--section-sticky-h) 기준 — 박스 높이(--hdr-h, 펼침값) 기준이면 조기 해제 딥 */
      var barHeight =
        Number.parseFloat(sectionStyle.getPropertyValue('--section-sticky-h')) || stickyHeader.offsetHeight;
      var expandedH = Number.parseFloat(sectionStyle.getPropertyValue('--hdr-h')) || stickyHeader.offsetHeight;
      /* 접힘 = 섹션 상단이 뷰포트 상단에 도달하면 (핀 상태 포함 — 큰 뷰포트에서도 접힌다).
         캡 모드 = 아직 v2 음수 top 잉여(--hdr-h − --section-sticky-h)를 화면 밖에 다 못 숨기는
         얕은 구간 — 바를 박스 상단에 접고 ::after 보상 (흰 패딩 노출 방지, 2026-07-23). */
      var isStuck = sectionRect.top <= 1 && sectionRect.bottom > barHeight + 20;
      var isCapped = isStuck && sectionRect.top > barHeight - expandedH + 2;
      section.classList.toggle('is-stuck', isStuck);
      section.classList.toggle('is-capped', isCapped);
    });
  }

  /* 카드 포커스(터치/키보드 게이트 보조) — 편집기 부분 리렌더에도 중복 없이 재연결 */
  function bindCardFocusHandlers() {
    document.querySelectorAll('.kuoca-cat-card, .kuoca-product-card').forEach(function (card) {
      if (card.dataset.kuocaFocusBound === 'true') return;
      card.dataset.kuocaFocusBound = 'true';
      card.addEventListener('focus', function () {
        card.classList.add('is-focused');
      });
      card.addEventListener('blur', function () {
        card.classList.remove('is-focused');
      });
    });
  }

  /* GNB 링크로 페이지를 떠나는 동안 스크롤 상태머신이 헤더를 다시 숨기지 않게 한다.
     새 PLP는 Liquid 단계부터 최종 solid/높이 상태로 렌더되어 두 페이지 사이에
     투명·숨김 중간 프레임이 생기지 않는다. */
  function bindHeaderNavigationHandlers() {
    if (!header || header.dataset.kuocaNavigationBound === 'true') return;
    header.dataset.kuocaNavigationBound = 'true';

    header.querySelectorAll('.kuoca-header__nav a').forEach(function (link) {
      link.addEventListener('click', function (event) {
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        )
          return;

        var destination;
        try {
          destination = new URL(link.href, window.location.href);
        } catch (error) {
          return;
        }
        if (destination.origin !== window.location.origin || destination.href === window.location.href) return;

        header.classList.remove('is-hidden');
        header.classList.add('is-navigating');
      });
    });

    if (!headerPageShowBound) {
      window.addEventListener('pageshow', function () {
        if (header) header.classList.remove('is-navigating');
      });
      headerPageShowBound = true;
    }
  }

  function updateStackingLayout() {
    updateStickyTops();
    updateSectionScrim();
    updateStickySections();
    updateGlobalHeader();
  }

  /* 이미지/컬렉션 교체 후 비동기로 높이가 달라져도 스택 수치를 다시 맞춘다.
     실제 스토어에는 Observer 자체를 만들지 않는다. */
  function observeEditorLayout() {
    if (!isThemeEditor || !window.ResizeObserver) return;

    editorResizeObserver = new ResizeObserver(function () {
      if (editorResizeTick) return;
      editorResizeTick = window.requestAnimationFrame(function () {
        editorResizeTick = 0;
        /* Hero 이미지 교체 뒤 이미지 로드/리사이즈가 연속 발생해도 레이아웃만 갱신한다.
           여기서 선택 섹션을 다시 reveal 하면 매 ResizeObserver 콜백마다 scrollTo 가
           실행되어 테마 편집기에서 사용자의 스크롤을 계속 되돌리게 된다. */
        updateStackingLayout();
      });
    });

    coverUnits.forEach(function (unit) {
      editorResizeObserver.observe(unit);
    });
    if (header) editorResizeObserver.observe(header);
  }

  /* ===== 히어로 캐러셀 (QA — G-16 해소) =====
     이미지 슬라이드는 data-hero-interval 만큼 머물고, 영상 슬라이드는 끝까지 재생한 뒤
     넘어간다(사용자 결정). 전환은 CSS 교차 페이드(.is-active / .is-prev).
     타이밍 값은 Figma·designRepo 에 명세가 없어 섹션 설정에서 읽는다 — 상수 금지.

     watchdog: ended 는 자동재생 차단·디코드 실패·소스 오류면 영영 오지 않는다. 그러면
     캐러셀이 그 슬라이드에서 멈추므로, 재생이 진행되는 동안만 연장되는 감시 타이머를 둔다. */
  var heroTimer = 0;
  var heroWatchdog = 0;
  var heroDetach = null;
  var heroManualDetach = null;
  var heroGaugeRaf = 0;

  function clearHeroTimers() {
    if (heroTimer) {
      clearTimeout(heroTimer);
      heroTimer = 0;
    }
    if (heroWatchdog) {
      clearTimeout(heroWatchdog);
      heroWatchdog = 0;
    }
    if (heroGaugeRaf) {
      cancelAnimationFrame(heroGaugeRaf);
      heroGaugeRaf = 0;
    }
    if (heroDetach) {
      heroDetach();
      heroDetach = null;
    }
  }

  function mountHeroCarousel() {
    clearHeroTimers();
    /* 이전 마운트의 수동 조작 리스너를 먼저 뗀다 — 아래 조기 반환 경로에서도 남으면 안 된다. */
    if (heroManualDetach) {
      heroManualDetach();
      heroManualDetach = null;
    }

    var hero = document.querySelector('[data-kuoca-hero]');
    if (!hero) return;
    var slides = Array.prototype.slice.call(hero.querySelectorAll('[data-hero-slide]'));
    var dots = Array.prototype.slice.call(hero.querySelectorAll('[data-hero-dot]'));
    if (slides.length < 2) return;

    var interval = Number.parseInt(hero.getAttribute('data-hero-interval'), 10) || 5000;
    var fade = Number.parseFloat(window.getComputedStyle(hero).getPropertyValue('--kuoca-hero-fade')) || 900;
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var current = 0;
    slides.forEach(function (slide, i) {
      if (slide.classList.contains('is-active')) current = i;
    });

    function videoIn(slide) {
      return slide.querySelector('video');
    }

    /* ===== 인디케이터 게이지 =====
       활성 바의 <i> 가 남은 시간만큼 차오른다. 이미지 슬라이드는 CSS 애니메이션에
       길이만 넘기고, 영상 슬라이드는 재생 진행률로 직접 그린다(재생이 버벅이거나
       버퍼링하면 게이지도 같이 멈춰야 시간과 어긋나지 않는다). */
    var fills = dots.map(function (dot) {
      return dot.querySelector('i');
    });

    function resetGauges() {
      if (heroGaugeRaf) {
        cancelAnimationFrame(heroGaugeRaf);
        heroGaugeRaf = 0;
      }
      fills.forEach(function (fill) {
        if (!fill) return;
        fill.style.animation = 'none';
        fill.style.transform = '';
        fill.style.removeProperty('--kuoca-hero-gauge-ms');
      });
    }

    /* 이미지 슬라이드 — CSS 애니메이션에 길이만 주고 맡긴다.
       animation:none 을 걷어내기 전에 강제 리플로를 넣어야 재시작된다(같은 프레임에
       none → '' 로만 바꾸면 브라우저가 변화를 못 보고 이전 애니메이션을 이어간다). */
    function startGauge(ms) {
      var fill = fills[current];
      if (!fill) return;
      fill.style.setProperty('--kuoca-hero-gauge-ms', ms + 'ms');
      void fill.offsetWidth;
      fill.style.animation = '';
    }

    /* 영상 슬라이드 — 재생 진행률로 매 프레임 그린다. */
    function driveGaugeWithVideo(video) {
      var fill = fills[current];
      if (!fill) return;
      fill.style.animation = 'none';
      (function paint() {
        var d = video.duration;
        var p = d && isFinite(d) && d > 0 ? Math.min(1, video.currentTime / d) : 0;
        fill.style.transform = 'scaleX(' + p + ')';
        heroGaugeRaf = requestAnimationFrame(paint);
      })();
    }

    function scheduleVideo(video) {
      function onEnded() {
        next();
      }
      /* 진행 중이면 감시 타이머를 계속 뒤로 민다. 첫 창은 버퍼링을 감안해 넉넉히. */
      function arm(window_ms) {
        if (heroWatchdog) clearTimeout(heroWatchdog);
        heroWatchdog = setTimeout(next, window_ms || 2500);
      }
      function onProgress() {
        arm(2500);
      }

      video.addEventListener('ended', onEnded);
      video.addEventListener('timeupdate', onProgress);
      heroDetach = function () {
        video.removeEventListener('ended', onEnded);
        video.removeEventListener('timeupdate', onProgress);
      };

      try {
        video.currentTime = 0;
      } catch (e) {
        /* 아직 seekable 이 아니면 무시 */
      }
      var played = video.play();
      if (played && played.catch)
        played.catch(function () {
          /* 자동재생 차단 — watchdog 이 넘긴다 */
        });
      arm(6000);
    }

    function schedule() {
      clearHeroTimers();
      resetGauges();
      if (reduceMotion) return; /* 자동 전환 없음 — 인디케이터 클릭만 */
      var video = videoIn(slides[current]);
      if (video) {
        scheduleVideo(video);
        driveGaugeWithVideo(video);
      } else {
        heroTimer = setTimeout(next, interval);
        startGauge(interval);
      }
    }

    /* 전환은 자동·수동 모두 크로스페이드다(QA: "스르륵 사라지고 나타나게").
       나가는 슬라이드는 .is-prev 로 제자리에 불투명하게 두고 들어오는 쪽만 페이드인한다 —
       양쪽을 동시에 페이드하면 중간 프레임에 히어로 배경색이 비친다. */
    function go(target) {
      if (target === current || !slides[target]) return;
      clearHeroTimers();

      var outgoing = slides[current];
      var incoming = slides[target];
      slides.forEach(function (slide) {
        slide.classList.remove('is-prev');
      });
      outgoing.classList.remove('is-active');
      outgoing.classList.add('is-prev');
      incoming.classList.add('is-active');

      var outgoingVideo = videoIn(outgoing);
      if (outgoingVideo) outgoingVideo.pause();

      dots.forEach(function (dot, i) {
        var on = i === target;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-selected', String(on));
      });

      current = target;
      /* 페이드가 끝난 뒤에야 이전 슬라이드를 내린다 — 먼저 내리면 그 순간 배경색이 비친다. */
      setTimeout(function () {
        outgoing.classList.remove('is-prev');
      }, fade);
      schedule();
    }

    function next() {
      go((current + 1) % slides.length);
    }

    function prev() {
      go((current - 1 + slides.length) % slides.length);
    }

    dots.forEach(function (dot, i) {
      if (dot.dataset.kuocaDotBound === 'true') return;
      dot.dataset.kuocaDotBound = 'true';
      dot.addEventListener('click', function () {
        go(i);
      });
    });

    /* ===== 수동 조작 (스와이프 / 드래그 / 방향키) =====
       go() 가 매번 schedule() 을 다시 걸므로 수동으로 넘긴 뒤 자동 전환 타이머는 초기화된다.

       터치에서는 preventDefault 를 하지 않는다 — 히어로가 화면을 꽉 채우는 커버 유닛이라
       가로 스와이프를 붙잡으면 세로 페이지 스크롤까지 같이 죽는다. 시작·끝 좌표만 재고
       가로 이동이 세로보다 확실히 클 때만 슬라이드를 넘긴다. */
    var SWIPE_MIN = 40; /* 이 미만은 탭으로 본다 */
    var pointerId = null;
    var startX = 0;
    var startY = 0;

    function onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      /* 마우스 드래그 시 이미지 고스트 드래그·텍스트 선택 방지. 인디케이터 클릭은 건드리지 않는다. */
      if (event.pointerType === 'mouse' && !(event.target.closest && event.target.closest('button'))) {
        event.preventDefault();
      }
    }

    function onPointerUp(event) {
      if (pointerId === null || event.pointerId !== pointerId) return;
      pointerId = null;
      var dx = event.clientX - startX;
      var dy = event.clientY - startY;
      if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) <= Math.abs(dy)) return;
      /* 손가락이 왼쪽으로 가면 다음 이미지가 오른쪽에서 덮어오고, 오른쪽이면 그 반대. */
      if (dx < 0) next();
      else prev();
    }

    function onPointerCancel() {
      pointerId = null;
    }

    /* 인디케이터는 role=tablist — 좌우 방향키로 이동하는 게 표준 동작이다. */
    function onKeyDown(event) {
      if (event.key === 'ArrowRight') {
        next();
        event.preventDefault();
      } else if (event.key === 'ArrowLeft') {
        prev();
        event.preventDefault();
      } else {
        return;
      }
      var active = dots[current];
      if (active && active.focus) active.focus();
    }

    hero.addEventListener('pointerdown', onPointerDown);
    hero.addEventListener('pointerup', onPointerUp);
    hero.addEventListener('pointercancel', onPointerCancel);
    hero.addEventListener('keydown', onKeyDown);
    heroManualDetach = function () {
      hero.removeEventListener('pointerdown', onPointerDown);
      hero.removeEventListener('pointerup', onPointerUp);
      hero.removeEventListener('pointercancel', onPointerCancel);
      hero.removeEventListener('keydown', onKeyDown);
    };

    schedule();
  }

  function rebuildStacking() {
    buildStackingScaffold();
    bindCardFocusHandlers();
    bindHeaderNavigationHandlers();
    mountHeroCarousel();
    updateStackingLayout();
    observeEditorLayout();
  }

  function naturalDocumentTop(element) {
    /* offsetTop/getBoundingClientRect 는 sticky 가 붙은 상태에서 시각 좌표를 반영할 수 있다.
       한 프레임도 내보내지 않는 동기 구간에서 sticky 만 제외해 flow 원래 좌표를 읽는다. */
    var wasUnit = element.classList.contains('kuoca-unit');
    var inlineTop = element.style.top;
    if (wasUnit) element.classList.remove('kuoca-unit');
    element.style.removeProperty('top');
    var top = window.scrollY + element.getBoundingClientRect().top;
    if (wasUnit) element.classList.add('kuoca-unit');
    if (inlineTop) element.style.top = inlineTop;
    return top;
  }

  function editorSectionOf(target) {
    if (!target) return null;
    if (typeof target === 'string') return document.getElementById(target);
    return target.classList && target.classList.contains('shopify-section')
      ? target
      : target.closest && target.closest('.shopify-section');
  }

  /* Shopify 의 자동 scrollIntoView 는 음수 top sticky wrapper 와 충돌한다.
     선택 섹션의 flow 상 원래 좌표로 보정하되 sticky/커버 효과는 그대로 둔다. */
  function revealEditorSection(target) {
    if (!isThemeEditor || !target) return;
    var section = editorSectionOf(target);
    if (!section || !section.isConnected) return;

    var headerOffset = header ? header.offsetHeight : 0;
    window.scrollTo({
      top: Math.max(0, naturalDocumentTop(section) - headerOffset),
      left: 0,
      behavior: 'auto',
    });

    window.requestAnimationFrame(function () {
      updateSectionScrim();
      updateStickySections();
      updateGlobalHeader();
    });
  }

  function scheduleEditorRebuild(target, revealSection) {
    var targetSection = editorSectionOf(target);
    var revealTarget = targetSection && targetSection.id ? targetSection.id : target;
    if (revealSection && targetSection && targetSection.id) {
      editorSelectedSectionId = targetSection.id;
    }
    if (editorRebuildTick) window.cancelAnimationFrame(editorRebuildTick);
    editorRebuildTick = window.requestAnimationFrame(function () {
      editorRebuildTick = window.requestAnimationFrame(function () {
        editorRebuildTick = 0;
        rebuildStacking();
        if (!revealSection) return;

        /* Shopify 도 section:load 뒤 자동 스크롤을 예약하므로 다음 두 프레임에서
           선택 섹션의 자연 위치를 다시 고정한다. */
        window.requestAnimationFrame(function () {
          revealEditorSection(revealTarget);
          window.requestAnimationFrame(function () {
            revealEditorSection(revealTarget);
          });
        });
      });
    });
  }

  function onScroll() {
    if (scrollTicking) return;
    window.requestAnimationFrame(function () {
      updateSectionScrim();
      updateStickySections();
      updateGlobalHeader();
      scrollTicking = false;
      window.requestAnimationFrame(function () {
        updateStickySections();
        updateGlobalHeader();
      });
    });
    scrollTicking = true;
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', function () {
    updateStickyTops();
    updateSectionScrim();
    updateStickySections();
    updateGlobalHeader();
  });
  window.addEventListener('load', updateStickyTops);

  /* 이 분기는 Shopify 테마 편집기 안에서만 실행된다. 실제 스토어의 스크롤 엔진에는
     이벤트/Observer/강제 위치 보정이 추가되지 않는다. */
  if (isThemeEditor) {
    document.addEventListener('shopify:section:load', function (event) {
      scheduleEditorRebuild(event.target, true);
    });
    document.addEventListener('shopify:section:unload', function () {
      scheduleEditorRebuild(null, false);
    });
    document.addEventListener('shopify:section:reorder', function (event) {
      scheduleEditorRebuild(event.target, true);
    });
    document.addEventListener('shopify:section:select', function (event) {
      scheduleEditorRebuild(event.target, true);
    });
    document.addEventListener('shopify:section:deselect', function (event) {
      var section = editorSectionOf(event.target);
      if (section && section.id === editorSelectedSectionId) editorSelectedSectionId = null;
    });
    document.addEventListener('shopify:block:select', function (event) {
      scheduleEditorRebuild(event.target, true);
    });
  }

  rebuildStacking();

  /* ============================================================== */
  /* ===== MODAL LAYER (menu / search) — 프리뷰 승인본 이식 ======== */
  /* 스크롤 잠금 = <html> position:fixed (Tekla), ref-count 공유.     */
  /* 트리거 = [data-kuoca-modal-open="<id>"], 닫기 = X/딤/ESC.       */
  /* ============================================================== */
  (function () {
    var rootEl = document.documentElement;
    var savedScrollY = 0,
      lockDepth = 0;
    function lockScroll() {
      if (lockDepth++ > 0) return;
      savedScrollY = window.scrollY;
      rootEl.style.position = 'fixed';
      rootEl.style.top = '-' + savedScrollY + 'px';
      rootEl.style.left = '0';
      rootEl.style.right = '0';
      rootEl.style.width = '100%';
      rootEl.style.overflow = 'hidden';
    }
    function unlockScroll() {
      if (lockDepth > 0) lockDepth--;
      if (lockDepth > 0) return;
      rootEl.style.position = '';
      rootEl.style.top = '';
      rootEl.style.left = '';
      rootEl.style.right = '';
      rootEl.style.width = '';
      rootEl.style.overflow = '';
      window.scrollTo(0, savedScrollY);
    }
    /* plp 필터 드로어 등 다른 오버레이와 잠금 규율 공유 (G-35) */
    window.kuocaScrollLock = { lock: lockScroll, unlock: unlockScroll };

    var modals = Array.prototype.slice.call(document.querySelectorAll('.kuoca-modal'));
    if (!modals.length) return;
    var active = null;
    function open(modal) {
      if (!modal || modal === active) return;
      if (active) closeActive(false); /* 동시 1개; 전환 중 잠금 유지 */
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      if (!active) lockScroll();
      active = modal;
      var focusable = modal.querySelector('[data-kuoca-modal-close], button, a, input');
      if (focusable) focusable.focus();
    }
    function closeActive(unlock) {
      if (unlock === undefined) unlock = true;
      if (!active) return;
      active.classList.remove('is-open');
      active.setAttribute('aria-hidden', 'true');
      active = null;
      if (unlock) unlockScroll();
    }
    document.querySelectorAll('[data-kuoca-modal-open]').forEach(function (el) {
      el.addEventListener('click', function () {
        open(document.getElementById(el.getAttribute('data-kuoca-modal-open')));
      });
    });
    modals.forEach(function (m) {
      m.querySelectorAll('[data-kuoca-modal-close]').forEach(function (el) {
        el.addEventListener('click', function () {
          closeActive();
        });
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && active) closeActive();
    });
  })();
})();
